import React, { useContext, useState } from 'react'

// @mui core components
import {
  Box,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import Card from 'atoms/Card'
import HelpIcon from '@mui/icons-material/Help'

// Data
import { IntakeContext } from 'context/IntakeContext'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import LoadingButton from 'components/LoadingButton'

const dollarCentsFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})
function PayStubDetails() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()
  const isGovernmentEmployee = intakeState?.governmentEmployee
  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your pay stub details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  const [localForm, setLocalForm] = useState({
    baseSalary: intakeState.baseSalary || '',
    highThree: intakeState.highThree || '',
    netTakeHome: intakeState.netTakeHome || '',
    currentLeaveBalance: intakeState.currentLeaveBalance || '',
    currentSickLeaveBalance: intakeState.currentSickLeaveBalance || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <>
      <Box
        pl={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography display="flex" align="left" variant="body2FunHeader">
          {isGovernmentEmployee ? 'LEAVE & EARNINGS' : 'EARNINGS'}
        </Typography>

        <LoadingButton loading={loading} onClick={onSubmit} />
      </Box>
      <Card>
        <Box component="form" p={3}>
          <Grid container columnSpacing={3} rowSpacing={2}>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                fullWidth: true,
                id: 'baseSalary',
                label: (
                  <>
                    <span>
                      Adjusted Base Annual Salary
                      <span style={{ color: 'red' }}> *</span>
                      <Tooltip
                        TransitionComponent={Fade}
                        title="Your Adjusted base salary is your base salary including any locality pay you receive."
                        placement="top"
                        color="info"
                      >
                        <IconButton size="small" color="dark">
                          <HelpIcon sx={{ marginTop: '-6px' }} />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </>
                ),
                placeholder: '$62,000.00',
                inputType: 'currencyFloat',
                value: localForm.baseSalary,
                onChange: (e) => handleChange('baseSalary')(e),
                onBlur: updateIntakeStateAndSubmit((baseSalary) =>
                  updateIntakeStateAtKey({ baseSalary })
                ),
              })}
            </Grid>
            {isGovernmentEmployee && (
              <>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'highThree',
                    label: (
                      <>
                        <span>
                          High Three
                          <span style={{ color: 'red' }}> *</span>
                          <Tooltip
                            TransitionComponent={Fade}
                            title="Highest three consecutive years of service. Used to calculate your pension"
                            placement="top"
                            color="info"
                          >
                            <IconButton size="small" color="dark">
                              <HelpIcon sx={{ marginTop: '-6px' }} />
                            </IconButton>
                          </Tooltip>
                        </span>
                      </>
                    ),
                    placeholder: dollarCentsFormat.format(
                      intakeState?.baseSalary
                        ? intakeState?.baseSalary * 0.96
                        : 62000
                    ),
                    inputType: 'currencyFloat',
                    value: localForm.highThree,
                    onChange: (e) => handleChange('highThree')(e),
                    onBlur: updateIntakeStateAndSubmit((highThree) =>
                      updateIntakeStateAtKey({ highThree })
                    ),
                  })}
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                id: 'netTakeHome',
                label: (
                  <>
                    <span>
                      Net take home per pay period
                      <span style={{ color: 'red' }}> *</span>
                      <Tooltip
                        TransitionComponent={Fade}
                        title="How much you take home in a normal pay period net of all deductions"
                        placement="top"
                        color="info"
                      >
                        <IconButton size="small" color="dark">
                          <HelpIcon sx={{ marginTop: '-6px' }} />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </>
                ),
                placeholder: '$2,400',
                inputType: 'currencyFloat',
                value: localForm.netTakeHome,
                onChange: (e) => handleChange('netTakeHome')(e),
                onBlur: updateIntakeStateAndSubmit((netTakeHome) =>
                  updateIntakeStateAtKey({ netTakeHome })
                ),
              })}
            </Grid>
            {isGovernmentEmployee && (
              <>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'currentLeaveBalance',
                    label: 'Current Earned Annual Leave Balance',
                    placeholder: '100.0',
                    inputType: 'floatField',
                    value: localForm.currentLeaveBalance,
                    onChange: (e) => handleChange('currentLeaveBalance')(e),
                    onBlur: updateIntakeStateAndSubmit((currentLeaveBalance) =>
                      updateIntakeStateAtKey({ currentLeaveBalance })
                    ),
                  })}
                </Grid>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'currentSickLeaveBalance',
                    label: 'Current Sick Leave Balance',
                    placeholder: '100.0',
                    inputType: 'floatField',
                    value: localForm.currentSickLeaveBalance,
                    onChange: (e) => handleChange('currentSickLeaveBalance')(e),
                    onBlur: updateIntakeStateAndSubmit(
                      (currentSickLeaveBalance) =>
                        updateIntakeStateAtKey({ currentSickLeaveBalance })
                    ),
                  })}
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default React.memo(PayStubDetails)
